import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';
import { siteConfig } from '../data/seo';

// Person schema for the named team members. We only emit entries for people
// whose full name we can confidently attribute (verified via /public/team
// asset filenames). Headshots and the team page itself are referenced via
// the standard Schema.org `image` and `mainEntityOfPage` keys so AI / Google
// can ground the entity to this page.
const teamPersonSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Person',
      '@id': `${siteConfig.url}/team#dr-erin-machek`,
      name: 'Dr. Erin Machek',
      givenName: 'Erin',
      familyName: 'Machek',
      jobTitle: 'Veterinarian',
      honorificPrefix: 'Dr.',
      image: `${siteConfig.url}/team/Dr.%20Erin%20Machek.jpeg`,
      worksFor: { '@type': 'VeterinaryCare', '@id': `${siteConfig.url}/#business` },
      mainEntityOfPage: `${siteConfig.url}/team`,
    },
    {
      '@type': 'Person',
      '@id': `${siteConfig.url}/team#dr-andrew-heller`,
      name: 'Dr. Andrew Heller',
      givenName: 'Andrew',
      familyName: 'Heller',
      jobTitle: 'Veterinarian and Co-Founder',
      honorificPrefix: 'Dr.',
      image: `${siteConfig.url}/team/Dr.%20Andrew%20Heller.jpeg`,
      worksFor: { '@type': 'VeterinaryCare', '@id': `${siteConfig.url}/#business` },
      mainEntityOfPage: `${siteConfig.url}/team`,
    },
    {
      '@type': 'Person',
      '@id': `${siteConfig.url}/team#anna-stryjek`,
      name: 'Anna Stryjek',
      givenName: 'Anna',
      familyName: 'Stryjek',
      jobTitle: 'Lead Veterinary Technician',
      image: `${siteConfig.url}/team/Anna%20Stryjek%20Vet%20Tech.jpeg`,
      worksFor: { '@type': 'VeterinaryCare', '@id': `${siteConfig.url}/#business` },
      mainEntityOfPage: `${siteConfig.url}/team`,
    },
    {
      '@type': 'Person',
      '@id': `${siteConfig.url}/team#sarah-george`,
      name: 'Sarah George',
      givenName: 'Sarah',
      familyName: 'George',
      jobTitle: 'Veterinary Technician',
      image: `${siteConfig.url}/team/Sarah%20George%20Vet%20Tech.jpeg`,
      worksFor: { '@type': 'VeterinaryCare', '@id': `${siteConfig.url}/#business` },
      mainEntityOfPage: `${siteConfig.url}/team`,
    },
  ],
};

const OurTeam: React.FC = () => {
  // Team members - images, names, titles only
  const teamMembers = [
    {
      name: "Dr. Machek",
      title: "Veterinarian",
      credentials: "",
      image: "/team/Dr. Machek.png"
    },
    {
      name: "Dr. Heller",
      title: "Veterinarian and Co-Founder",
      credentials: "",
      image: "/team/Dr. Andrew Heller.jpeg"
    },
    {
      name: "Anna",
      title: "Lead Veterinary Technician",
      credentials: "",
      image: "/team/Anna Stryjek Vet Tech.jpeg"
    },
    {
      name: "Sarah",
      title: "Veterinary Technician",
      credentials: "",
      image: "/team/Sarah George Vet Tech.jpeg"
    },
    {
      name: "Stacey",
      title: "Community",
      credentials: "",
      image: "/team/Stacey.png"
    },
    {
      name: "Tom",
      title: "Tech Support",
      credentials: "",
      image: "/team/Tom.png"
    },
    {
      name: "Samson",
      title: "Our Beautiful Blind Bouvier",
      credentials: "",
      image: "/team/Samson.png"
    }
  ];

  const values = [
    {
      icon: "fas fa-heartbeat",
      title: "Health First",
      description: "We only do what helps. No cosmetic fluff.",
      color: "from-teal-500 to-teal-600",
      bgColor: "bg-teal-500"
    },
    {
      icon: "fas fa-eye",
      title: "Transparency Always", 
      description: "Flat prices. Honest assessments. Clear decisions.",
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-500"
    },
    {
      icon: "fas fa-paw",
      title: "Pets Deserve Better",
      description: "We treat them like family — because they are.",
      color: "from-yellow-500 to-yellow-600",
      bgColor: "bg-yellow-500"
    },
    {
      icon: "fas fa-heart",
      title: "Owners Matter Too",
      description: "You'll feel heard, supported, and fully informed.",
      color: "from-red-500 to-red-600",
      bgColor: "bg-red-500"
    }
  ];

  const positions = [
    {
      title: "Licensed Veterinarian - Dental Specialist",
      type: "Full-time",
      location: "Fort Lauderdale, FL",
      salary: "$120,000 - $180,000",
      description: "Join our specialized dental team focusing exclusively on veterinary dentistry. Perfect for vets passionate about dental health.",
      requirements: [
        "DVM degree and active FL license",
        "Interest in or experience with veterinary dentistry",
        "Commitment to continued education",
        "Excellent communication skills with pet parents"
      ]
    },
    {
      title: "Veterinary Technician - Dental Focus",
      type: "Full-time",
      location: "Fort Lauderdale, FL", 
      salary: "$45,000 - $65,000",
      description: "Support our dental procedures with specialized technical skills. Growth opportunities in dental radiography and anesthesia monitoring.",
      requirements: [
        "Certified Veterinary Technician (CVT/RVT)",
        "Experience with anesthesia monitoring", 
        "Dental radiography experience preferred",
        "Team-oriented mindset"
      ]
    },
    {
      title: "Client Care Coordinator",
      type: "Full-time",
      location: "Fort Lauderdale, FL",
      salary: "$35,000 - $45,000", 
      description: "Be the friendly face that guides pet parents through their dental care journey. Focus on education and compassionate service.",
      requirements: [
        "Customer service experience",
        "Excellent communication skills",
        "Empathy for pet parents' concerns",
        "Administrative and scheduling abilities"
      ]
    }
  ];

  const benefits = [
    "Competitive salary and performance bonuses",
    "Comprehensive health, dental, and vision insurance",
    "Continuing education allowance",
    "Professional development opportunities",
    "Flexible scheduling options",
    "Pet care discounts",
    "Collaborative, supportive work environment",
    "Opportunity to specialize in veterinary dentistry"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-soft-gray to-white">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(teamPersonSchema)}
        </script>
      </Helmet>
      {/* Header */}
      <section className="bg-brand-teal text-white py-16">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Our Team
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Meet the passionate professionals dedicated to your pet's dental health
          </p>
        </div>
      </section>

      {/* Meet Our Veterinarians */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-brand-teal/10 text-brand-teal px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="fas fa-user-md"></i>
              Licensed Veterinary Professionals
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The passionate people behind your pet's dental care
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
                {/* 2x bigger team member photo */}
                <div className="w-64 h-64 mx-auto mb-4 rounded-full overflow-hidden shadow-xl">
                  {member.image ? (
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <i className="fas fa-user text-gray-400 text-6xl"></i>
                    </div>
                  )}
                </div>

                {/* Name and credentials */}
                <h3 className="text-xl font-bold text-primary mb-1">
                  {member.name}
                  {member.credentials && (
                    <span className="text-brand-teal ml-2">{member.credentials}</span>
                  )}
                </h3>
                <p className="text-base text-brand-teal font-semibold">{member.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials & Certifications */}
      <section className="section-padding bg-gradient-to-br from-brand-teal/5 to-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-primary mb-8">Our Credentials</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <i className="fas fa-certificate text-brand-teal text-3xl mb-3"></i>
                <p className="font-semibold text-gray-800">Licensed by Florida DBPR</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <i className="fas fa-shield-alt text-brand-teal text-3xl mb-3"></i>
                <p className="font-semibold text-gray-800">Fully Insured</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <i className="fas fa-x-ray text-brand-teal text-3xl mb-3"></i>
                <p className="font-semibold text-gray-800">Most Advanced Pet Dental X-Ray in the State</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <i className="fas fa-heartbeat text-brand-teal text-3xl mb-3"></i>
                <p className="font-semibold text-gray-800">Advanced Monitoring</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About CHOMP */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-8">
              About CHOMP
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12">
              Dedicated to providing affordable dental care for your pets
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg mb-12">
              <h3 className="text-2xl md:text-3xl font-bold text-primary mb-6 text-center">
                OUR MISSION
              </h3>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  At Chomp Pet Dental, our mission is to provide affordable, high-quality dental care for pets.
                  We believe that proper dental care is essential for your pet's overall health and well-being.
                </p>
                <p>
                  Our team of experienced veterinarians and technicians are passionate about dentistry and
                  dedicated to ensuring your pet receives the best possible care. We use state-of-the-art
                  equipment and techniques to provide comprehensive dental services.
                </p>
                <p>
                  From routine cleanings to complex extractions, we're committed to eliminating pain and
                  infection from your pet's mouth and preventing future health issues, helping them live
                  a healthier, happier life.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="bg-white section-padding relative">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-8">
              OUR VALUES
            </h2>
            
            <div className="max-w-4xl mx-auto mb-12">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
                What "Chomp" Means To Us
              </h3>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  The name "Chomp" embodies everything we want for your pet: vitality, strength, energy, 
                  and health. A strong bite is a sign of a healthy animal – one that can eat well, play 
                  enthusiastically, and live life to the fullest. When your pet can chomp confidently without 
                  pain, it means they're thriving.
                </p>
                <p>
                  Our mission goes beyond just cleaning teeth. We're committed to restoring and 
                  maintaining that powerful, healthy bite that allows your pet to enjoy food, toys, and life 
                  with vigor. A good "chomp" represents overall wellbeing, and that's exactly what we help 
                  deliver.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center group cursor-pointer"
              >
                <div className="mb-8">
                  <div className={`w-24 h-24 mx-auto rounded-full ${value.bgColor} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 mb-6`}>
                    <i className={`${value.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-primary mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed font-medium">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Hidden Easter Egg - Tiny dog in bottom right */}
          <Link
            to="/spot"
            className="absolute bottom-8 right-8 text-xs opacity-20 hover:opacity-100 hover:scale-150 transition-all duration-300 cursor-pointer"
            title="🤫"
          >
            🐕
          </Link>
        </div>
      </section>

      {/* Why Join CHOMP */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Join Our Mission
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Be part of a revolutionary approach to veterinary dentistry. We're not just another vet clinic - we're specialists focused on one thing: extending pets' lives through exceptional dental care.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <i className="fas fa-graduation-cap text-brand-teal text-xl"></i>
                  <span className="font-semibold">Specialize & grow your expertise in veterinary dentistry</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fas fa-users text-brand-teal text-xl"></i>
                  <span className="font-semibold">Work with a passionate, collaborative team</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fas fa-chart-line text-brand-teal text-xl"></i>
                  <span className="font-semibold">Be part of a growing, innovative practice</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fas fa-heart text-brand-teal text-xl"></i>
                  <span className="font-semibold">Make a real difference in pets' and families' lives</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-brand-teal to-forest-green rounded-2xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-4">Our Impact</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-4xl font-bold">🐕</div>
                    <div className="text-sm opacity-90">Happy Pets Treated</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold">5 Years</div>
                    <div className="text-sm opacity-90">Added to Pet Lives</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold">50%</div>
                    <div className="text-sm opacity-90">Faster Than Traditional</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold">100%</div>
                    <div className="text-sm opacity-90">Transparent Pricing</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
              Open Positions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our team and help us provide the best dental care for pets in South Florida
            </p>
          </div>
          
          <div className="space-y-8 max-w-4xl mx-auto">
            {positions.map((position, index) => (
              <div key={index} className="bg-card-bg rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                      <h3 className="text-2xl font-bold text-primary">
                        {position.title}
                      </h3>
                      <div className="flex gap-2">
                        <span className="bg-brand-teal text-white px-3 py-1 rounded-full text-sm font-medium">
                          {position.type}
                        </span>
                        <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                          {position.location}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-xl font-semibold text-green-600 mb-4">
                      {position.salary}
                    </div>
                    
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      {position.description}
                    </p>
                    
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">Requirements:</h4>
                      <ul className="space-y-2">
                        {position.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <i className="fas fa-check text-brand-teal mt-1"></i>
                            <span className="text-gray-600">{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="lg:ml-8">
                    <a href="mailto:careers@chomp.vet" className="btn-primary w-full lg:w-auto px-8 py-3 inline-flex items-center justify-center">
                      <i className="fas fa-paper-plane mr-2"></i>
                      Apply Now
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
              Benefits & Perks
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We invest in our team because great care starts with great people
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 shadow-md">
                <i className="fas fa-star text-brand-teal text-2xl mb-4"></i>
                <p className="text-gray-700 font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply CTA */}
      <section className="bg-gradient-to-r from-brand-teal to-forest-green text-white section-padding">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Join our mission to revolutionize pet dental care and add years to pets' lives. 
            Even if you don't see a perfect fit above, we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:careers@chomp.vet" className="bg-white text-brand-teal px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center">
              <i className="fas fa-envelope mr-2"></i>
              Send Your Resume
            </a>
            <a href="tel:+19544663775" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-brand-teal transition-all duration-300 inline-flex items-center justify-center">
              <i className="fas fa-phone mr-2"></i>
              Call Us: (954) 466-3775
            </a>
          </div>
          
          <div className="mt-8 text-center">
            <p className="opacity-80">
              📧 careers@chomp.vet | 📍 1800 E Commercial Blvd, Fort Lauderdale, FL 33308
            </p>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default OurTeam;